import React, { Fragment } from 'react';
import { object, number, oneOfType, string, bool } from 'prop-types';
import { ifElse, has, prop, identity, pipe, invoker, split, defaultTo } from 'ramda';
import styled from 'styled-components';

const PriceWrapper = styled.div`
  margin-top: ${({ marginTop }) => (marginTop || '22px')};
  display: flex;
  flex-direction: column;
`;

const ReferenceText = styled.span`
  color: #4a4a4a;
  font-size: 14px;
  line-height: 16px;
  ${({ lineThrough }) => lineThrough && 'text-decoration: line-through;'}
  ${({ bold }) => bold && 'font-weight: bold;'}
`;

const NormalText = styled.span`
  margin: 0 1px;
  font-size: ${({ isReferencePrice }) => {
    if (isReferencePrice) {
      return '15px';
    }

    return '16px';
  }};
  color: #4a4a4a;
  line-height: 1.38;
  ${({ lineThrough }) => lineThrough && 'text-decoration: line-through;'}
`;

const HighlitedText = styled(NormalText)`
  font-size: ${({ isReferencePrice }) => {
    if (isReferencePrice) {
      return '15px';
    }

    return '46px';
  }};
  line-height: 1;
`;

const LighterText = styled(NormalText)`
  color: #999999;
`;

const getPrice = ifElse(
  has('previous'),
  prop('previous'),
  identity,
);

const splitPrice = pipe(
  getPrice,
  defaultTo(0),
  invoker(1, 'toFixed')(2),
  split('.'),
);

const getCurrenceFormat = value => value.toFixed(2).replace('.', ',');

const Price = ({
  value = 0.00, recurrence, isReferencePrice,
}) => {
  if (typeof value === 'object') {
    const [currentIntPart, currentFloatPart] = splitPrice(value.current);
    const [previousIntPart, previousFloatPart] = splitPrice(value.previous);
    return (
      <Fragment>
        <PriceWrapper>
          <div>
            <ReferenceText>De R$ </ReferenceText>
            <ReferenceText lineThrough>{previousIntPart},{previousFloatPart}</ReferenceText>
          </div>
        </PriceWrapper>
        <PriceWrapper marginTop={'15px'}>
          <div>
            <NormalText>Por </NormalText>
            <NormalText>R$</NormalText>
            <HighlitedText>{currentIntPart}</HighlitedText>
            <NormalText>,{currentFloatPart}</NormalText>
          </div>
        </PriceWrapper>
      </Fragment>
    );
  }

  const [intPart, floatPart] = splitPrice(value);

  if (isReferencePrice) {
    return (
      <PriceWrapper>
        <div>
          <NormalText isReferencePrice>De R$</NormalText>
          <NormalText isReferencePrice lineThrough>{getCurrenceFormat(value)}</NormalText>
          {recurrence && (<LighterText>{`/ ${recurrence}`}</LighterText>)}
        </div>
      </PriceWrapper>
    );
  }

  return (
    <PriceWrapper>
      <div>
        <NormalText>R$</NormalText>
        <HighlitedText>{intPart}</HighlitedText>
        <NormalText>,{floatPart}</NormalText>
        {recurrence && (<LighterText>{`/ ${recurrence}`}</LighterText>)}
      </div>
    </PriceWrapper>
  );
};

Price.propTypes = {
  value: oneOfType([object, number]).isRequired,
  recurrence: string,
  isReferencePrice: bool,
};

Price.defaultProps = {
  recurrence: null,
  isReferencePrice: false,
};

export default Price;
